import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';

import type { ArcContentItem, ArcResourceType } from '@/api/generated';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Dialog } from '@/components/ui/Dialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { Field } from '@/components/ui/Field';
import { StatusChip } from '@/components/ui/StatusChip';
import { formatDate } from '@/lib/organization';
import { useDebounced } from '@/lib/useDebounced';

import { limitReasons } from './arcSchemas';
import { resourceTypeLabel, serverSentence, type Arc, type ArcSection } from './arcTypes';
import { holds } from './draftEdits';
import { useContentSearch } from './useContentSearch';

/** The types in the order the portal's navigation lists their sections. */
const TYPE_ORDER = [
  'CAMPAIGN',
  'PETITION',
  'EVENT',
  'GUIDE',
  'SURVEY',
  'PAGE',
  'DOCUMENT',
];

function typesFor(section: ArcSection): ArcResourceType[] {
  return TYPE_ORDER.filter((type) => section.accepts.includes(type)) as ArcResourceType[];
}

/** "campaigns", "petitions": a type's plural for a sentence. */
function plural(type: string): string {
  return `${resourceTypeLabel(type).toLowerCase()}s`;
}

interface AddContentDialogProps {
  /** The section as the working copy holds it now, so a fresh add shows at once. */
  section: ArcSection;
  /** The working copy as it is now, and the limits in force, from the reply. */
  arc: Pick<Arc, 'sections' | 'limits'>;
  /** Puts the resource last in the section's working copy; Save Draft saves it. */
  onAdd: (picked: ArcContentItem) => void;
  onClose: () => void;
}

/**
 * The Add Content picker (Day 27): the organization's published content of
 * the types this section takes, searched on the server and paged, each with
 * an Add button. Adding changes the working copy only, so several can be
 * added in one opening and Save Draft sends them with everything else.
 *
 * Only resources published in their own module are listed (E.16), and a type
 * whose module does not exist yet says so instead of showing a table: the
 * server answers `available` false for it, or, since group 6 (B7, D10),
 * leaves it out of the section's `accepts` altogether, so a typed section
 * whose type has no adapter accepts nothing and asks the server nothing.
 */

/** The one type a standard section is for: "CAMPAIGNS" holds campaigns. */
function typeOfSection(section: ArcSection): string | null {
  return section.standard_type && section.standard_type !== 'FEATURED'
    ? section.standard_type.replace(/S$/, '')
    : null;
}
export function AddContentDialog({
  section,
  arc,
  onAdd,
  onClose,
}: AddContentDialogProps) {
  // From the copy as it is now, so the picker stops at the limit while open (B16).
  const limit = limitReasons(arc).addTo(section.id);
  const types = typesFor(section);
  const [type, setType] = useState<ArcResourceType | null>(types[0] ?? null);
  const [typed, setTyped] = useState('');
  const [page, setPage] = useState(1);
  const [added, setAdded] = useState<Set<string>>(() => new Set());
  const search = useDebounced(typed.trim(), 300);
  const results = useContentSearch(type, search, page);

  const label = type ? resourceTypeLabel(type) : 'Content';
  const data = results.data;
  // Nothing this section may hold has a module yet, or the chosen type's
  // module answered that it has none.
  const nothingAccepted = types.length === 0;
  const unavailable = nothingAccepted || data?.available === false;
  const unavailableWords = nothingAccepted
    ? typeOfSection(section)
      ? `${resourceTypeLabel(typeOfSection(section) ?? '')}s are not available yet.`
      : 'No content is available yet.'
    : `${resourceTypeLabel(type ?? '')}s are not available yet.`;
  const rows = data?.items ?? [];
  const keyOf = (row: ArcContentItem) => `${row.resource_type}:${row.resource_id}`;

  const add = (row: ArcContentItem) => {
    onAdd(row);
    setAdded((current) => new Set(current).add(keyOf(row)));
  };

  const columns: Column<ArcContentItem>[] = [
    { key: 'content', header: 'Content', render: (row) => row.title },
    {
      key: 'type',
      header: 'Type',
      render: (row) => resourceTypeLabel(row.resource_type),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <StatusChip
          label={row.status_label}
          tone="success"
          testId="arc-builder-add-content-status-chip"
        />
      ),
    },
    { key: 'details', header: 'Details', render: (row) => row.details ?? '—' },
    {
      key: 'updated',
      header: 'Last updated',
      render: (row) => formatDate(row.updated_at),
    },
    {
      key: 'add',
      header: 'Add',
      align: 'right',
      render: (row) => {
        const justAdded = added.has(keyOf(row));
        const already = !justAdded && holds(section, row.resource_type, row.resource_id);
        const words = justAdded ? 'Added' : already ? 'In this section' : 'Add';
        // Just added: unavailable but still focused, so a keyboard user goes
        // on from this row rather than from the top of the dialog.
        return (
          <Button
            size="sm"
            variant={justAdded || already ? 'ghost' : 'secondary'}
            disabled={already || (limit !== null && !justAdded)}
            aria-disabled={justAdded || undefined}
            onClick={() => {
              if (!justAdded) add(row);
            }}
            aria-label={words === 'Add' ? `Add ${row.title}` : `${words}: ${row.title}`}
            data-testid="arc-builder-add-content-add-button"
          >
            {words}
          </Button>
        );
      },
    },
  ];

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Add to ${section.name}`}
      description="Content published in its own section of the portal. Save the draft to keep what you add."
      maxWidth="md"
      actions={
        <Button onClick={onClose} data-testid="arc-builder-add-content-done-button">
          Done
        </Button>
      }
      testId="arc-builder-add-content-dialog"
    >
      {limit ? (
        <Alert
          severity="info"
          role="status"
          sx={{ mb: 2 }}
          data-testid="arc-builder-add-content-limit-message"
        >
          {limit}
        </Alert>
      ) : null}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: types.length > 1 ? '2fr 1fr' : '1fr' },
          mb: 2,
        }}
      >
        <Field label={`Search ${plural(label)}`} testId="arc-builder-add-content-search">
          <TextField
            value={typed}
            onChange={(event) => {
              setTyped(event.target.value);
              setPage(1);
            }}
            size="small"
            fullWidth
            type="search"
            // The backend refuses a longer search.
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
        </Field>
        {types.length > 1 ? (
          <Field label="Type" testId="arc-builder-add-content-type">
            <TextField
              select
              value={type ?? ''}
              onChange={(event) => {
                setType(event.target.value as ArcResourceType);
                setPage(1);
              }}
              size="small"
              fullWidth
            >
              {types.map((option) => (
                <MenuItem key={option} value={option}>
                  {resourceTypeLabel(option)}
                </MenuItem>
              ))}
            </TextField>
          </Field>
        ) : null}
      </Box>

      {unavailable ? (
        <EmptyState
          title={unavailableWords}
          description="They arrive with their own section of the portal."
          testId="arc-builder-add-content-unavailable"
        />
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={rows}
            getRowKey={(row) => row.resource_id}
            caption={`Published ${plural(label)}`}
            loading={results.isPending}
            refreshing={results.isFetching && !results.isPending}
            error={
              results.isError
                ? serverSentence(results.error, 'The content could not be loaded.')
                : null
            }
            onRetry={() => void results.refetch()}
            emptyMessage={`No published ${plural(label)} yet`}
            noMatchMessage={`Nothing matches “${search}”`}
            filtered={search !== ''}
            testId="arc-builder-add-content-table"
          />
          {page > 1 || data?.pagination.has_more ? (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                justifyContent: 'flex-end',
                mt: 1.5,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                data-testid="arc-builder-add-content-table-page-info"
              >
                Page {page}
              </Typography>
              <Button
                size="sm"
                variant="secondary"
                disabled={page === 1}
                onClick={() => setPage((current) => current - 1)}
                data-testid="arc-builder-add-content-table-previous-button"
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={!data?.pagination.has_more}
                onClick={() => setPage((current) => current + 1)}
                data-testid="arc-builder-add-content-table-next-button"
              >
                Next
              </Button>
            </Box>
          ) : null}
        </>
      )}
    </Dialog>
  );
}
